import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    const optionName = String(event.target?.lastChangedOption || '')
      .trim()
      .toLowerCase();
    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    if (!source) return;

    // Finish and Size still update the variant, price, availability, and product form,
    // but on mobile they keep the currently visible media exactly as it is.
    if (isMobile && (optionName === 'finish' || optionName === 'size')) {
      return;
    }

    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);

    // Color keeps Shopify's normal variant-media behavior, then returns the customer
    // to the refreshed gallery so the selected color is immediately visible.
    if (isMobile && optionName === 'color') this.#scrollToGallery(newMediaGallery);
  };

  /**
   * Smoothly returns a mobile customer to the top of the product gallery.
   *
   * @param {Element} gallery - The current or newly rendered media gallery.
   */
  #scrollToGallery(gallery) {
    requestAnimationFrame(() => {
      const target = gallery.closest('[data-testid="product-information-media"]') || gallery;
      const styles = getComputedStyle(document.body);
      const headerHeight = Number.parseFloat(styles.getPropertyValue('--header-height')) || 0;
      const headerGroupHeight = Number.parseFloat(styles.getPropertyValue('--header-group-height')) || 0;
      const offset = Math.max(headerHeight, headerGroupHeight) + 12;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
